import { type ReactElement } from 'react';
import {
  MetricsPageToolbarPanel,
  type MetricsPageToolbarPanelProps,
} from '@/features/shared/components/metricsToolbar/MetricsPageToolbar';
import { type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { s } from '@/theme/tokens';
import {
  coverageRunFailureKindFilterOptions,
  coverageRunFailureSubkindFilterOptions,
} from './coverageRunFailurePresentation';
import type { CoverageRunFailureKind, CoverageRunFailureSubkind } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type FilterOption = {
  label: string;
  value: string;
};

type CoveragePageFiltersToolbarProps = {
  coverageRunOptions: FilterOption[];
  dateQuickFilter: DateQuickFilter;
  filterCoverageRuns: string[];
  directoryOptions?: FilterOption[];
  fileOptions?: FilterOption[];
  filterTags: string[];
  filterDirectories?: string[];
  filterFiles?: string[];
  filterFailureKinds: CoverageRunFailureKind[];
  filterFailureSubkinds: CoverageRunFailureSubkind[];
  fromDate: Dayjs | null;
  onFilterTagsChange: (nextTags: string[]) => void;
  onDateQuickFilterChange: (nextQuickFilter: DateQuickFilter) => void;
  onFilterCoverageRunsChange: (nextRunIds: string[]) => void;
  onFilterDirectoriesChange?: (nextDirectories: string[]) => void;
  onFilterFilesChange?: (nextFiles: string[]) => void;
  onFilterFailureKindsChange: (nextFailureKinds: CoverageRunFailureKind[]) => void;
  onFilterFailureSubkindsChange: (nextFailureSubkinds: CoverageRunFailureSubkind[]) => void;
  onFromDateChange: (nextDate: Dayjs | null) => void;
  onToDateChange: (nextDate: Dayjs | null) => void;
  tagOptions: FilterOption[];
  toDate: Dayjs | null;
  showDirectoryFilter?: boolean;
  showFileFilter?: boolean;
};

const EMPTY_STATUS_FILTERS: string[] = [];
const EMPTY_STATUS_OPTIONS: Array<{ value: string; label: string }> = [];
type CoverageToolbarFilterSlot = NonNullable<
  MetricsPageToolbarPanelProps<string>['filterSlots']
>[number];

export const CoveragePageFiltersToolbar = ({
  coverageRunOptions,
  dateQuickFilter,
  filterTags,
  filterCoverageRuns,
  filterFailureKinds,
  filterFailureSubkinds,
  directoryOptions,
  fileOptions,
  filterDirectories = [],
  filterFiles = [],
  fromDate,
  onFilterTagsChange,
  onDateQuickFilterChange,
  onFilterCoverageRunsChange,
  onFilterDirectoriesChange,
  onFilterFilesChange,
  onFilterFailureKindsChange,
  onFilterFailureSubkindsChange,
  onFromDateChange,
  onToDateChange,
  showDirectoryFilter = true,
  showFileFilter = true,
  tagOptions,
  toDate,
}: CoveragePageFiltersToolbarProps): ReactElement => {
  const failureTaxonomyFilterSlots: CoverageToolbarFilterSlot[] = [
    {
      id: 'failure-kind',
      label: getFilterLabel('failureKind'),
      inputAriaLabel: 'Filter by failure type',
      value: filterFailureKinds,
      defaultValue: [],
      options: coverageRunFailureKindFilterOptions,
      emptyLabel: getFilterEmptyLabel('allFailureKinds'),
      onChange: (nextFailureKinds) => {
        onFilterFailureKindsChange(nextFailureKinds as CoverageRunFailureKind[]);
      },
    },
    {
      id: 'failure-subkind',
      label: getFilterLabel('failureSubkind'),
      inputAriaLabel: 'Filter by failure detail',
      value: filterFailureSubkinds,
      defaultValue: [],
      options: coverageRunFailureSubkindFilterOptions,
      emptyLabel: getFilterEmptyLabel('allFailureDetails'),
      onChange: (nextFailureSubkinds) => {
        onFilterFailureSubkindsChange(nextFailureSubkinds as CoverageRunFailureSubkind[]);
      },
    },
  ];

  return (
    <MetricsPageToolbarPanel
      title=""
      panelPadding="xs"
      panelPaddingX={s('md')}
      headerTitlePaddingLeft={s('none')}
      searchAriaLabel="Search coverage runs"
      showInlineFilters
      showSearch={false}
      searchValue=""
      onDebouncedSearchChange={() => undefined}
      filterStatuses={EMPTY_STATUS_FILTERS}
      statusOptions={EMPTY_STATUS_OPTIONS}
      onFilterStatusesChange={() => undefined}
      filterSuites={filterCoverageRuns}
      onFilterSuitesChange={onFilterCoverageRunsChange}
      suiteOptions={coverageRunOptions}
      suiteLabel="Coverage Run"
      filterEntities={showDirectoryFilter ? filterDirectories : []}
      onFilterEntitiesChange={showDirectoryFilter ? onFilterDirectoriesChange : undefined}
      entityOptions={showDirectoryFilter ? (directoryOptions ?? []) : []}
      entityLabel={showDirectoryFilter ? 'Directory' : undefined}
      filterItems={showFileFilter ? filterFiles : []}
      onFilterItemsChange={showFileFilter ? onFilterFilesChange : undefined}
      itemOptions={showFileFilter ? (fileOptions ?? []) : []}
      itemLabel={showFileFilter ? 'File' : undefined}
      filterSlots={failureTaxonomyFilterSlots}
      filterTags={filterTags}
      onFilterTagsChange={onFilterTagsChange}
      dateQuickFilter={dateQuickFilter}
      onDateQuickFilterChange={onDateQuickFilterChange}
      fromDate={fromDate}
      toDate={toDate}
      onFromDateChange={onFromDateChange}
      onToDateChange={onToDateChange}
      availableTags={tagOptions.map((option) => option.value)}
    />
  );
};
